// src/themes/modern/restaurant/components/RestaurantHeader.tsx
import { motion } from "framer-motion";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaArrowRight, FaHeart, FaShareAlt } from "react-icons/fa";
import { IoLanguageSharp } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import type { Branch } from "~/types/new_types";
import ShareModal from "./ShareModal";
import { getBranchDescription, getBranchName } from "~/utils/brandHelper";
import { useBranch } from "~/contexts/BranchContext";
import { BiShoppingBag } from "react-icons/bi";
import { useShoppingCart } from "~/contexts/ShoppingCartContext";
import CurrencySwitcher from "~/components/CurrencySwitcher";

// Geometric pattern animation
const GeometricPlaceholder = ({ name }: { name: string }) => {
  const { theme } = useBranch();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="w-full h-[300px] relative bg-gradient-to-br from-emerald-400 to-cyan-400 overflow-hidden"
    >
      {/* Animated geometric shapes */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute top-1/4 left-1/4 w-8 h-8 border-2 border-white/20"
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        className="absolute top-3/4 right-1/4 w-6 h-6 bg-white/10 rounded-full"
      />
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360],
        }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute top-1/2 right-1/3 w-4 h-4 bg-white/15 transform rotate-45"
      />
    </motion.div>
  );
};

const RestaurantHeader: React.FC = () =>
// { branch }
{
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const location = useLocation();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const navigate = useNavigate();

  const { branch, isLoading, error } = useBranch();

  // Helper function to check if restaurant is currently open
  const getRestaurantStatus = () => {
    if (!branch?.operating_hours || branch.operating_hours.length === 0) {
      return null; // No operating hours available
    }

    const now = new Date();
    const currentDay = now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes

    const todayHours = branch.operating_hours.find(
      (item) => item.day.toLowerCase() === currentDay
    );

    if (!todayHours) {
      return null; // No hours for today
    }

    if (todayHours.is_closed) {
      return { isOpen: false, message: t("restaurant.info.closed") };
    }

    // Parse opening and closing times (format: "HH:MM" or "H:MM")
    const parseTime = (timeStr: string) => {
      const [hours, minutes] = timeStr.split(":").map(Number);
      return hours * 60 + minutes;
    };

    try {
      const openingTime = parseTime(todayHours.opening_time);
      const closingTime = parseTime(todayHours.closing_time);

      // Handle cases where closing time is after midnight (e.g., 23:00 - 02:00)
      let isOpen: boolean;
      if (closingTime < openingTime) {
        // Restaurant closes after midnight
        isOpen = currentTime >= openingTime || currentTime < closingTime;
      } else {
        // Normal hours
        isOpen = currentTime >= openingTime && currentTime < closingTime;
      }

      return {
        isOpen,
        message: isOpen ? t("restaurant.info.open") : t("restaurant.info.closed"),
      };
    } catch {
      return null; // Invalid time format
    }
  };

  const restaurantStatus = getRestaurantStatus();

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  const goToCart = () => {
    navigate(`/${branch?.id}/cart`);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  // Return null on error
  if (error) {
    return null;
  }

  // Render skeleton while loading
  if (isLoading || !branch) {
    return null;
  }

  return (
    <div
      className="relative"
      style={{ backgroundColor: branch?.theme?.backgroundColor }}
    >
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        branch={branch!}
        i18nIsDynamicList
      />

      {/* Cover Image */}
      <div className="relative h-fit">
        {branch?.cover_image ? (
          <motion.img
            src={branch?.cover_image}
            alt={branch?.name}
            className="w-full aspect-[4/1] min-h-[160px] object-cover object-center"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "/images/broken-image.jpg";
            }}
          />
        ) : (
          <GeometricPlaceholder name={branch?.name} />
        )}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))`,
            backdropFilter: "blur(0px)",
          }}
        />

        {/* Header Actions */}
        <div className="absolute top-4 left-0 right-0 px-4 z-10">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            {branch?.has_landing && (
              <motion.button
                className="bg-white/10 backdrop-blur-lg hover:bg-white/20 p-3 rounded-full text-white cursor-pointer transition-colors"
                onClick={() => {
                  if (location.pathname.endsWith("/menu"))
                    navigate(`/${branch?.id}`);
                  else
                    navigate(`/${branch?.id}`);
                }}
              >
                {isRTL ? <FaArrowRight /> : <FaArrowLeft />}
              </motion.button>
            )}


            <motion.div
              className="flex gap-3"
            >
              <motion.button
                className="bg-white/10 h-10 w-10 backdrop-blur-lg hover:bg-white/20 p-3 rounded-full text-white cursor-pointer transition-colors"
                onClick={toggleLanguage}
              >
                <IoLanguageSharp />
              </motion.button>
              {/* c */}
              <CurrencySwitcher /> 
            
              <motion.button
                className="bg-white/10 h-10 w-10 backdrop-blur-lg hover:bg-white/20 p-3 rounded-full text-white cursor-pointer transition-colors"
                onClick={() => setIsShareModalOpen(true)}
              >
                <FaShareAlt />
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* Restaurant Logo */}
        <motion.div
          className={`absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 px-4 z-10`}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="w-32 h-32 md:w-44 md:h-44 rounded-full overflow-hidden border-6 shadow-xl bg-white shrink-0"
            variants={itemVariants}
            style={{ borderColor: branch?.theme.backgroundColor }}
          >
            <img
              src={
                branch?.logo ? branch?.logo : "/images/no-image-available.jpg"
              }
              alt={`${branch?.name} logo`}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "/images/broken-image.jpg";
              }}
            />
          </motion.div>
        </motion.div>
      </div>

      <div
        className="overflow-hidden"
        style={{ backgroundColor: branch?.theme.cardColor }}
      >
        <motion.div
          className={`px-4 pt-18 md:pt-24 pb-2 text-center z-10`}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              variants={itemVariants}
              style={{
                color: branch?.theme.textColor,
              }}
            >
              <h1 className={`font-bold mb-2 ${branch.theme.titleFontSize}`} style={{ fontFamily: branch.theme.titleFontFamily }}>
                {getBranchName(branch!, i18n.language)}
              </h1>
              {/* Open/Closed Status Badge */}
              {restaurantStatus && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                  className="flex justify-center mb-2"
                >
                  <span
                    className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold ${
                      restaurantStatus.isOpen
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                    style={{
                      borderRadius: branch.theme.borderRadius,
                    }}
                  >
                    {/* Pulsing dot indicator */}
                    <span
                      className={`w-2 h-2 rounded-full ${
                        restaurantStatus.isOpen ? "bg-green-500" : "bg-red-500"
                      }`}
                      style={{
                        animation: restaurantStatus.isOpen
                          ? "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
                          : "none",
                      }}
                    />
                    {restaurantStatus.message}
                  </span>
                </motion.div>
              )}
              <p className="text-lg md:text-xl mb-2">
							{getBranchDescription(branch, i18n.language)}
						</p>
            </motion.div>
          </div>
        </motion.div>

        {/* Wave separator */}
        <div
          className="relative h-16 -mt-8"
        >
          <svg
            viewBox="0 0 1440 100"
            className="absolute bottom-0 left-0 w-full h-full -my-10 -scale-100 "
            preserveAspectRatio="none"
            fill={branch?.theme.backgroundColor}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 100L48 95.7C96 91.3 192 82.7 288 78.3C384 74 480 74 576 78.3C672 82.7 768 91.3 864 91.3C960 91.3 1056 82.7 1152 76.7C1248 70.7 1344 67 1392 65.2L1440 63.3V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V100Z"
              fillOpacity="1"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default RestaurantHeader;
